/**
 * Where a person goes after the OAuth round trip lands them on `/`.
 *
 * The sign-in page marks `PENDING_SIGNIN_KEY` before it hands the browser
 * to the provider and keeps the intended destination under
 * `NEXT_STORAGE_KEY`; the callback deliberately returns privileged accounts
 * to `/`. Whichever page renders `/` — the full landing page or the
 * production Coming Soon page — must therefore finish the journey, and it
 * must do so the same way. This is that one way.
 *
 * The rules:
 *
 *   - Nothing happens until the session has resolved; a loading session
 *     must not redirect, and an anonymous one has nowhere to go.
 *   - Only a sign-in started in this tab redirects: the pending marker is
 *     the proof, and it is consumed exactly once, so a re-run of the effect
 *     (Strict Mode, a session refresh) finds nothing and does nothing. A
 *     signed-in person who simply visits `/` stays on the page.
 *   - A merchant goes to `/home`. Anyone else goes to the preserved safe
 *     `next` path when there is one, else `/dashboard` — unless onboarding
 *     is still owed, in which case `/onboarding` comes first and the
 *     onboarding finish consumes the destination instead (removing it here
 *     is exactly the bug that once stranded a first-time contributor on the
 *     dashboard when they had been invited straight to a task).
 *   - Storage may be unavailable (private modes); then there is nothing to
 *     resume and the page is a complete state of its own.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSession, type SessionState } from '../session.js';
import { NEXT_STORAGE_KEY, PENDING_SIGNIN_KEY, safeNextPath } from './SignInPage.js';

export interface PendingSignIn {
  readonly pending: boolean;
  /** The preserved destination, already filtered by `safeNextPath`. */
  readonly next: string | null;
}

/** Read the pending marker and destination; never throws. */
export function readPendingSignIn(
  storage: Storage | null = storageOrNull(),
): PendingSignIn {
  if (storage === null) return { pending: false, next: null };
  try {
    return {
      pending: storage.getItem(PENDING_SIGNIN_KEY) === '1',
      next: safeNextPath(storage.getItem(NEXT_STORAGE_KEY)),
    };
  } catch {
    return { pending: false, next: null };
  }
}

/**
 * The destination for a resolved, authenticated session that started its
 * sign-in in this tab. Pure.
 */
export function pendingSignInDestination(
  session: Extract<SessionState, { status: 'authenticated' }>,
  next: string | null,
): { readonly path: string; readonly consumesNext: boolean } {
  if (session.account.role === 'MERCHANT') return { path: '/home', consumesNext: true };
  if (!session.profile.onboardingCompleted)
    return { path: '/onboarding', consumesNext: false };
  return { path: next ?? '/dashboard', consumesNext: true };
}

function storageOrNull(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Finish a sign-in that landed on `/`: consume the marker once and navigate.
 * Idempotent under Strict Mode and session refreshes because the marker is
 * gone after the first run.
 */
export function usePendingSignInRedirect(): void {
  const { session } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (session.status !== 'authenticated') return;
    const storage = storageOrNull();
    const { pending, next } = readPendingSignIn(storage);
    if (!pending) return;
    const destination = pendingSignInDestination(session, next);
    try {
      storage?.removeItem(PENDING_SIGNIN_KEY);
      if (destination.consumesNext) storage?.removeItem(NEXT_STORAGE_KEY);
    } catch {
      // Storage refused the write; the navigation still happens once.
    }
    void navigate(destination.path, { replace: true });
  }, [session, navigate]);
}
